import { useState } from "react";

import ChevronDown from "@uui/ChevronDown";

import RecommendationActionMenu from "@app/analytics/Recommendations/components/Recommendation/RecommendationActionMenu";
import type { RecommendationActionType } from "@app/analytics/Recommendations/types";
import type {
  Recommendation as IRecommendation,
  RecommendationAction,
} from "@app/analytics/types";
import * as RecommendationUtils from "@app/analytics/utils/recommendationUtils";
import { TOOLTIPS } from "@app/common/content";
import useAnchorElement from "@app/utils/hooks/useAnchorElement";

import { Row } from "@rui/Stack";
import TooltipWrapper from "@rui/TooltipWrapper";

import { ActionButton } from "./styles";

export interface RecommendationActionsProps {
  actionLoading?: boolean;
  compact?: boolean;
  isSample: boolean;
  onAccept?: (action: RecommendationAction) => void;
  onReject?: (recommendation: IRecommendation) => void;
  recommendation: IRecommendation;
}

const RecommendationActions = ({
  actionLoading = false,
  compact = false,
  isSample,
  onAccept,
  onReject,
  recommendation,
}: RecommendationActionsProps) => {
  const acceptActionMenu = useAnchorElement();
  const [actionTaken, setActionTaken] =
    useState<RecommendationActionType | null>();
  const defaultAction = RecommendationUtils.getDefaultAction(recommendation);
  const hasMultipleActions = recommendation.actions.length > 1;

  const handleAcceptAction = (action: RecommendationAction) => {
    setActionTaken("accept");
    onAccept?.(action);
    acceptActionMenu.onClose();
  };

  const handleClickAcknowledge = () => {
    setActionTaken("acknowledge");
    onAccept?.(defaultAction);
  };

  const handleClickAccept = (event: React.MouseEvent<HTMLElement>) => {
    if (hasMultipleActions) {
      acceptActionMenu.onClick(event);
      return;
    }

    handleAcceptAction(defaultAction);
  };

  const handleReject = () => {
    setActionTaken("reject");
    onReject?.(recommendation);
  };

  return (
    <>
      <Row variant="right" spacing={1}>
        {defaultAction.kind === "acknowledge" ? (
          <TooltipWrapper
            show={isSample}
            title={TOOLTIPS.recommendations.sampleActionDisabled}
          >
            <ActionButton
              compact={compact}
              kind="acknowledge"
              disabled={isSample}
              onClick={handleClickAcknowledge}
              title="Acknowledge"
              loading={actionTaken === "acknowledge" && actionLoading}
            >
              {compact ? "" : "Acknowledge"}
            </ActionButton>
          </TooltipWrapper>
        ) : (
          <TooltipWrapper
            show={isSample}
            title={TOOLTIPS.recommendations.sampleActionDisabled}
          >
            <ActionButton
              compact={compact}
              kind="accept"
              disabled={isSample}
              onClick={handleClickAccept}
              title="Accept"
              endIcon={
                hasMultipleActions ? (
                  <ChevronDown className="recommendation-action__expand-icon" />
                ) : undefined
              }
              loading={actionTaken === "accept" && actionLoading}
            >
              {compact ? "" : "Accept"}
            </ActionButton>
          </TooltipWrapper>
        )}
        <TooltipWrapper
          show={isSample}
          title={TOOLTIPS.recommendations.sampleActionDisabled}
        >
          <ActionButton
            compact={compact}
            kind="reject"
            disabled={isSample}
            onClick={handleReject}
            title="Ignore"
            loading={actionTaken === "reject" && actionLoading}
          >
            {compact ? "" : "Ignore"}
          </ActionButton>
        </TooltipWrapper>
      </Row>
      <RecommendationActionMenu
        anchorEl={acceptActionMenu.anchorElement}
        recommendation={recommendation}
        onClickAction={handleAcceptAction}
        open={acceptActionMenu.open}
        onClose={acceptActionMenu.onClose}
      />
    </>
  );
};

export default RecommendationActions;
